import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { numberToCurrencyString } from 'further-ui/utils';
import { UnrealisedCashFXGainOrLossTypeLabels } from 'further-ui/labels';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import Typography from '@mui/material/Typography';
import { SortOrder } from 'further-types/utils';

const pageId = 'unrealised-cash-fx-history';

type Props = {
  vehicleId: string;
  onViewRecord?: (recordId: string) => void;
};

type RecordToDelete = {
  id: string;
};

const CashHistory: FC<Props> = ({ vehicleId, onViewRecord }) => {
  const { vehicle } = useLayoutContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<RecordToDelete | null>(
    null,
  );

  const pagination = usePagination({
    id: pageId,
    order: SortOrder.Desc,
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedCashFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord, isPending: isDeleting } =
    api.unrealisedCashFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Record deleted successfully.',
        onSuccess: () => {
          refetch();
          handleCloseDeleteDialog();
        },
      }),
    );

  const handleOpenDeleteDialog = (record: RecordToDelete) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setRecordToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    if (!recordToDelete) return;

    deleteRecord({
      vehicleId,
      recordId: recordToDelete.id,
    });
  };

  const rows = data?.results ?? [];

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Ledger Date',
      render: (row) => row.ledgerDate?.toLabel?.() ?? '-',
    },
    {
      key: 'bankAccountName',
      label: 'Bank Account',
      render: (row) => row.bankAccountName ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossType',
      label: 'Type',
      render: (row) => UnrealisedCashFXGainOrLossTypeLabels[row.gainOrLossType],
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) => {
        const isLoss =
          row.gainOrLossType === 'UnrealisedCashFXLoss' ||
          row.gainOrLossType === 'UnrealisedCashFXUnwindLoss';
        const amount = isLoss ? -row.amountLcy : row.amountLcy;
        return numberToCurrencyString(amount, {
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      disableSorting: true,
    },
  ];

  if (!vehicle || (!isLoading && !data?.results?.length)) {
    return null;
  }

  return (
    <Column spacing="lg" fullWidth>
      <Typography variant="h5">
        Unrealised cash FX gains and losses history
      </Typography>
      <Table
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        actions={[
          {
            label: 'View',
            icon: Visibility,
            onClick: onViewRecord ? (row) => onViewRecord(row.id) : undefined,
          },
          {
            label: 'Delete',
            icon: Delete,
            disabled: (row) => !row.canDelete,
            tooltip: (row) =>
              row.canDelete ? undefined : row.deleteReason ?? 'Cannot delete',
            onClick: (row) =>
              handleOpenDeleteDialog({
                id: row.id,
              }),
          },
        ]}
        pagination={{
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          order: pagination.order,
          orderBy: pagination.orderBy,
          handleRequestSort: pagination.handleRequestSort,
          handleChangePage: pagination.handleChangePage,
          handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
        }}
        rowsTotalCount={data?.totalCount ?? 0}
      />
      <ConfirmDialog
        onClose={handleCloseDeleteDialog}
        open={deleteDialogOpen}
        title="Delete unrealised cash FX gain/loss?"
        content="Are you sure you want to delete this unrealised cash FX gain/loss? This cannot be undone."
        loading={isDeleting}
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
          },
          cancel: {
            onClick: handleCloseDeleteDialog,
          },
        }}
      />
    </Column>
  );
};

export default CashHistory;
